import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { routeService } from '../../services/route.service';
import { geocodingService, LocationSuggestion } from '../../services/geocoding.service';
import { Route } from '../../types';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { LocationAutocomplete } from '../../components/ui/LocationAutocomplete';
import toast from 'react-hot-toast';

const routeSchema = z.object({
  origin: z.string().min(1, 'Origin is required'),
  destination: z.string().min(1, 'Destination is required'),
  officialDistanceKm: z
    .preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.number().positive('Distance must be positive')
    )
    .optional(),
  roundTrip: z.boolean(),
  description: z.string().optional(),
});

type RouteFormData = z.input<typeof routeSchema>;
type RouteFormParsed = z.infer<typeof routeSchema>;

interface RouteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  route: Route | null;
}

export const RouteFormModal: React.FC<RouteFormModalProps> = ({ isOpen, onClose, route }) => {
  const queryClient = useQueryClient();
  const isEdit = !!route;
  const [originLocation, setOriginLocation] = useState<LocationSuggestion | null>(null);
  const [destLocation, setDestLocation] = useState<LocationSuggestion | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<RouteFormData>({
    resolver: zodResolver(routeSchema),
    defaultValues: route || { roundTrip: false },
  });

  const origin = watch('origin');
  const destination = watch('destination');

  const createMutation = useMutation({
    mutationFn: routeService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      toast.success('Route created successfully');
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create route');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: RouteFormParsed) => routeService.update(route!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      toast.success('Route updated successfully');
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update route');
    },
  });

  const calculateDistance = async () => {
    if (!originLocation || !destLocation) return;

    setIsCalculating(true);
    try {
      const result = await geocodingService.calculateDistance(
        originLocation.lat,
        originLocation.lon,
        destLocation.lat,
        destLocation.lon
      );

      if (result) {
        setValue('officialDistanceKm', result.distanceKm);
        toast.success(`Distance calculated: ${result.distanceKm} km`);
      } else {
        toast.error('Could not calculate distance. Please enter manually.');
      }
    } catch (error) {
      toast.error('Failed to calculate distance');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleOriginSelect = async (location: LocationSuggestion) => {
    setOriginLocation(location);
  };

  const handleDestSelect = async (location: LocationSuggestion) => {
    setDestLocation(location);
  };

  const onSubmit = (data: RouteFormData) => {
    const parsed = routeSchema.parse(data);
    if (isEdit) {
      updateMutation.mutate(parsed);
    } else {
      createMutation.mutate(parsed);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Route' : 'Add New Route'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            loading={createMutation.isPending || updateMutation.isPending}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <LocationAutocomplete
            label="Origin"
            value={origin || ''}
            onChange={(value) => setValue('origin', value)}
            onLocationSelect={handleOriginSelect}
            error={errors.origin?.message}
            placeholder="e.g., Dar es Salaam"
            required
          />
          <LocationAutocomplete
            label="Destination"
            value={destination || ''}
            onChange={(value) => setValue('destination', value)}
            onLocationSelect={handleDestSelect}
            error={errors.destination?.message}
            placeholder="e.g., Dodoma"
            required
          />
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={calculateDistance}
            disabled={!originLocation || !destLocation || isCalculating}
          >
            Calculate distance
          </Button>
        </div>

        {isCalculating && (
          <div className="text-sm text-primary flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
            Calculating distance...
          </div>
        )}

        <Input
          label="Official Distance (km)"
          type="number"
          step="0.1"
          placeholder="e.g., 486"
          error={errors.officialDistanceKm?.message}
          {...register('officialDistanceKm', {
            setValueAs: (v) => (v === '' || v === null ? undefined : Number(v)),
          })}
        />

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="roundTrip"
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            {...register('roundTrip')}
          />
          <label htmlFor="roundTrip" className="text-sm font-medium text-neutral-charcoal">
            Round Trip
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-charcoal mb-1">
            Description
          </label>
          <textarea
            rows={3}
            placeholder="Optional route description..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            {...register('description')}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-error">{errors.description.message}</p>
          )}
        </div>
      </form>
    </Modal>
  );
};
